import {
  initializeApp,
  type FirebaseApp,
  type FirebaseOptions,
} from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  browserLocalPersistence,
  setPersistence,
  type Auth,
} from 'firebase/auth';

// Config injetada via Vite env vars (`VITE_FIREBASE_*`). Sem ela, o app
// monta em modo "auth desabilitada":
//  - rotas públicas (`/`, `/privacy`, `/live`) funcionam normais
//  - rotas guarded (`AuthGuard`) caem na tela de "Acesso restrito" /
//    redirecionam pra login (que mostra erro amigável)
//
// Pré-req crítico: CI roda Playwright sem `.env.local`. Antes desse fix,
// `initializeApp(readConfig())` lançava no module load → React não
// montava → todos os e2e falhavam. Agora a init é lazy + tolerante;
// auth-context.tsx e consumers (EventLiveAdminPage) checam null.

interface FirebaseHandles {
  app: FirebaseApp;
  auth: Auth;
  googleProvider: GoogleAuthProvider;
}

function readConfig(): FirebaseOptions | null {
  const required = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_APP_ID',
  ] as const;
  const missing = required.filter((k) => !import.meta.env[k]);
  if (missing.length > 0) {
    console.warn(
      `[firebase] config ausente (${missing.join(', ')}). ` +
        `Auth desabilitada em runtime. Defina em ` +
        `infra/dashboard/admin/.env.local pra habilitar (PERMISSIONS_CHECKLIST.md).`,
    );
    return null;
  }
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  };
}

function init(): FirebaseHandles | null {
  const config = readConfig();
  if (!config) return null;
  try {
    const app = initializeApp(config);
    const auth = getAuth(app);
    // Persist token entre reloads — Wizard de cadastro pode levar minutos,
    // reload acidental sem persistência mata progresso.
    setPersistence(auth, browserLocalPersistence).catch((err) => {
      console.warn('[firebase] falha setando persistência local', err);
    });
    return { app, auth, googleProvider: new GoogleAuthProvider() };
  } catch (err) {
    console.error('[firebase] init falhou, app continuará em modo no-auth', err);
    return null;
  }
}

const handles = init();

/// Null quando env vars não estão setadas (CI sem .env.local, dev local
/// sem config Firebase). Consumers DEVEM checar antes de usar — ver
/// `auth-context.tsx` e `EventLiveAdminPage.tsx`.
export const firebaseApp: FirebaseApp | null = handles?.app ?? null;
export const firebaseAuth: Auth | null = handles?.auth ?? null;
export const googleProvider: GoogleAuthProvider | null = handles?.googleProvider ?? null;

/// Helper de readonly check pra UI mostrar mensagem amigável quando
/// auth tá indisponível (login button disabled etc).
export const firebaseConfigured = handles !== null;
